'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import type { LocaleContentMap } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export interface ComparisonConfig {
  slug: string;
  competitorName: string;
  heroTitle: string;
  heroSubtitle: string;
  overviewComparison: { label: string; hotelstack: string; competitor: string }[];
  featureTable: {
    category: string;
    features: { name: string; hotelstack: boolean | string; competitor: boolean | string }[];
  }[];
  pricingComparison: {
    hotelstackStarting: string;
    competitorStarting: string;
    hotelstackNote: string;
    competitorNote: string;
  };
  advantages: string[];
  verdict: { title: string; summary: string; bestFor: string[] };
  faqItems: { question: string; answer: string }[];
}

/* -------------------------------------------------------------------------- */
/*  SVG Icons                                                                 */
/* -------------------------------------------------------------------------- */

function CheckSVG() {
  const { t } = useTranslation();
  return (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5 text-hms-green" aria-label={t('common.yes')}>
      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
      <path d="M6 10.5l2.5 2.5L14 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrossSVG() {
  const { t } = useTranslation();
  return (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5 text-hms-red" aria-label={t('common.no')}>
      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function FeatureCell({ value }: { value: boolean | string }) {
  if (typeof value === 'boolean') {
    return value ? <CheckSVG /> : <CrossSVG />;
  }
  return <span className="text-xs font-medium text-hms-textSub">{value}</span>;
}

/* -------------------------------------------------------------------------- */
/*  Section — Hero                                                            */
/* -------------------------------------------------------------------------- */

function CompareHero({ config, t }: { config: ComparisonConfig; t: TemplateChromeContent }) {
  return (
    <section id={`compare-${config.slug}-hero`} className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <p id={`compare-${config.slug}-badge`} className="mb-4 text-xs font-semibold uppercase tracking-widest text-hms-gold">
          {t.common.comparison}
        </p>
        <h1
          id={`compare-${config.slug}-title`}
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {config.heroTitle}
        </h1>
        <p id={`compare-${config.slug}-subtitle`} className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {config.heroSubtitle}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`compare-${config.slug}-cta-primary`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`compare-${config.slug}-cta-demo`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.bookDemo}
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Quick Comparison                                                */
/* -------------------------------------------------------------------------- */

function QuickComparison({ config, t }: { config: ComparisonConfig; t: TemplateChromeContent }) {
  return (
    <section id={`compare-${config.slug}-overview`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-overview-title`}
          className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl"
        >
          {t.common.quickComparison}
        </h2>
        <div className="mt-12 overflow-hidden rounded-xl border border-hms-border">
          {/* Header row */}
          <div className="grid grid-cols-3 border-b border-hms-border bg-hms-card/80 px-5 py-3.5">
            <div className="text-xs font-semibold uppercase tracking-wider text-hms-textDim">{t.common.criteria}</div>
            <div className="text-center text-xs font-bold uppercase tracking-wider text-hms-gold">HotelStack.io</div>
            <div className="text-center text-xs font-semibold uppercase tracking-wider text-hms-textSub">{config.competitorName}</div>
          </div>
          {/* Data rows */}
          {config.overviewComparison.map((row, idx) => (
            <div
              key={row.label}
              id={`compare-${config.slug}-overview-row-${idx}`}
              className={cn(
                'grid grid-cols-3 items-center px-5 py-3.5',
                idx % 2 === 0 ? 'bg-hms-card/30' : 'bg-hms-card/10',
                idx < config.overviewComparison.length - 1 && 'border-b border-hms-border/50',
              )}
            >
              <p className="text-sm font-medium text-hms-text">{row.label}</p>
              <p className="text-center text-sm font-semibold text-hms-gold">{row.hotelstack}</p>
              <p className="text-center text-sm text-hms-textSub">{row.competitor}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Feature Comparison Table                                        */
/* -------------------------------------------------------------------------- */

function FeatureComparisonTable({ config, t }: { config: ComparisonConfig; t: TemplateChromeContent }) {
  return (
    <section id={`compare-${config.slug}-features`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-features-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.featureComparison}
        </h2>
        <div className="mt-14 space-y-8">
          {config.featureTable.map((group, gIdx) => (
            <div key={group.category} id={`compare-${config.slug}-fgroup-${gIdx}`} className="overflow-hidden rounded-xl border border-hms-border">
              {/* Category header */}
              <div className="border-b border-hms-border bg-hms-card px-5 py-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-hms-text">{group.category}</h3>
              </div>
              {/* Column labels */}
              <div className="grid grid-cols-[1fr_100px_100px] border-b border-hms-border/50 bg-hms-card/60 px-5 py-2.5 sm:grid-cols-[1fr_140px_140px]">
                <span className="text-xs font-semibold uppercase tracking-wider text-hms-textDim">{t.common.feature}</span>
                <span className="text-center text-xs font-bold uppercase tracking-wider text-hms-gold">HotelStack</span>
                <span className="text-center text-xs font-semibold uppercase tracking-wider text-hms-textSub">{config.competitorName}</span>
              </div>
              {/* Feature rows */}
              {group.features.map((feat, fIdx) => (
                <div
                  key={feat.name}
                  id={`compare-${config.slug}-feat-${gIdx}-${fIdx}`}
                  className={cn(
                    'grid grid-cols-[1fr_100px_100px] items-center px-5 py-3 sm:grid-cols-[1fr_140px_140px]',
                    fIdx % 2 === 0 ? 'bg-hms-card/20' : 'bg-transparent',
                    fIdx < group.features.length - 1 && 'border-b border-hms-border/30',
                  )}
                >
                  <span className="text-sm text-hms-textSub">{feat.name}</span>
                  <span className="flex items-center justify-center"><FeatureCell value={feat.hotelstack} /></span>
                  <span className="flex items-center justify-center"><FeatureCell value={feat.competitor} /></span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Pricing Comparison                                              */
/* -------------------------------------------------------------------------- */

function PricingComparison({ config, t }: { config: ComparisonConfig; t: TemplateChromeContent }) {
  const p = config.pricingComparison;
  return (
    <section id={`compare-${config.slug}-pricing`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-pricing-title`}
          className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl"
        >
          {t.common.pricing}
        </h2>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* HotelStack card */}
          <div
            id={`compare-${config.slug}-pricing-hs`}
            className="rounded-xl border-2 border-hms-gold/40 bg-hms-card p-6 text-center shadow-lg shadow-hms-gold/5"
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-hms-gold">HotelStack.io</p>
            <p className={cn(
              'mt-4 text-3xl font-extrabold tracking-tight',
              'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
            )}>
              {p.hotelstackStarting}
            </p>
            <p className="mt-1 text-xs text-hms-textDim">{t.common.startingPricePerMonth}</p>
            <p className="mt-4 text-sm leading-relaxed text-hms-textSub">{p.hotelstackNote}</p>
          </div>
          {/* Competitor card */}
          <div
            id={`compare-${config.slug}-pricing-comp`}
            className="rounded-xl border border-hms-border bg-hms-card p-6 text-center"
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-hms-textSub">{config.competitorName}</p>
            <p className="mt-4 text-3xl font-extrabold tracking-tight text-hms-text">
              {p.competitorStarting}
            </p>
            <p className="mt-1 text-xs text-hms-textDim">{t.common.startingPricePerMonth}</p>
            <p className="mt-4 text-sm leading-relaxed text-hms-textSub">{p.competitorNote}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Why HotelStack.io                                               */
/* -------------------------------------------------------------------------- */

function WhyHotelStack({ config, t }: { config: ComparisonConfig; t: TemplateChromeContent }) {
  return (
    <section id={`compare-${config.slug}-advantages`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-advantages-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.whyChoose}
        </h2>
        <div className="mt-12 space-y-4">
          {config.advantages.map((adv, idx) => (
            <div
              key={idx}
              id={`compare-${config.slug}-advantage-${idx}`}
              className="flex items-start gap-3 rounded-xl border border-hms-border bg-hms-card p-4 transition-colors hover:border-hms-gold/20"
            >
              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hms-gold/10">
                <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3 text-hms-gold">
                  <path d="M4 8.5l2.5 2.5L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="text-sm leading-relaxed text-hms-textSub">{adv}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Verdict                                                         */
/* -------------------------------------------------------------------------- */

function VerdictSection({ config, t }: { config: ComparisonConfig; t: TemplateChromeContent }) {
  const v = config.verdict;
  return (
    <section id={`compare-${config.slug}-verdict`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-xl border border-hms-gold/30 bg-hms-card p-8 shadow-lg shadow-hms-gold/5">
          <h2 id={`compare-${config.slug}-verdict-title`} className="text-center text-2xl font-bold tracking-tight text-hms-gold sm:text-3xl">
            {v.title}
          </h2>
          <p className="mt-4 text-center text-base leading-relaxed text-hms-textSub">{v.summary}</p>
          <div className="mt-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-hms-textDim">{t.common.bestFor}</p>
            <div className="space-y-2">
              {v.bestFor.map((item, idx) => (
                <div key={idx} id={`compare-${config.slug}-verdict-item-${idx}`} className="flex items-center gap-3">
                  <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5 shrink-0 text-hms-teal">
                    <path d="M4 8.5l2.5 2.5L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-sm text-hms-textSub">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — FAQ                                                             */
/* -------------------------------------------------------------------------- */

function CompareFAQ({ config, t }: { config: ComparisonConfig; t: TemplateChromeContent }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = useCallback((i: number) => setOpenIndex((p) => (p === i ? null : i)), []);

  return (
    <section id={`compare-${config.slug}-faq`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-faq-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.faq}
        </h2>
        <div className="mt-12 space-y-3">
          {config.faqItems.map((item, idx) => (
            <div
              key={idx}
              id={`compare-${config.slug}-faq-item-${idx}`}
              className={cn(
                'rounded-xl border transition-colors',
                openIndex === idx ? 'border-hms-gold/30 bg-hms-card' : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15',
              )}
            >
              <button
                id={`compare-${config.slug}-faq-toggle-${idx}`}
                type="button"
                onClick={() => toggle(idx)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openIndex === idx}
              >
                <span className={cn('text-sm font-semibold leading-snug', openIndex === idx ? 'text-hms-gold' : 'text-hms-text')}>
                  {item.question}
                </span>
                <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform duration-200', openIndex === idx ? 'rotate-180 text-hms-gold' : 'text-hms-textDim')} />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-hms-textSub">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — CTA                                                             */
/* -------------------------------------------------------------------------- */

function CompareCTA({ config, t }: { config: ComparisonConfig; t: TemplateChromeContent }) {
  return (
    <section id={`compare-${config.slug}-cta`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2
          id={`compare-${config.slug}-cta-title`}
          className={cn(
            'text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.readyToStart}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">
          {t.common.joinHotels}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`compare-${config.slug}-cta-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`compare-${config.slug}-cta-contact`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.contactBtn}
          </LocaleLink>
        </div>
        <p className="mt-5 text-xs text-hms-textDim">{t.common.noCreditCard}</p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function ComparisonPage({ configMap }: { configMap: LocaleContentMap<ComparisonConfig> }) {
  const config = usePageContent(configMap);
  const t = usePageContent(TEMPLATE_CHROME);

  return (
    <main id={`compare-${config.slug}-page`}>
      <CompareHero config={config} t={t} />
      <QuickComparison config={config} t={t} />
      <FeatureComparisonTable config={config} t={t} />
      <PricingComparison config={config} t={t} />
      <WhyHotelStack config={config} t={t} />
      <VerdictSection config={config} t={t} />
      <CompareFAQ config={config} t={t} />
      <CompareCTA config={config} t={t} />
    </main>
  );
}
